import styled, { css } from 'styled-components';

import { Text } from '@/nova/components/Text/Text';
import { ANIM, COLORS, MOBILE, SPACE } from '@/nova/theme/tokens';

/** Column: label above the trigger, matching the shared Select's layout so it drops in beside Fields. In
 *  `$compact` mode it lays the label INLINE to the left of a smaller trigger, to sit in the modal header row. */
export const Root = styled.div<{ $compact?: boolean }>`
  display: flex;
  ${(p) => (p.$compact ? 'flex-direction: row; align-items: center; gap: 8px;' : 'flex-direction: column; gap: 8px;')}
  min-width: 0;
`;

/** Icon + label row for the field caption, so a field can carry a small leading glyph. */
export const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${COLORS.textMuted};
`;

export const Label = styled(Text)`
  font-size: 14px;
  color: ${COLORS.text};
`;

/** The closed control, styled to match the shared Select's inset control so it reads as the same field. */
export const Trigger = styled.button<{ $open: boolean; $compact?: boolean; $mobileIcon?: boolean }>`
  position: relative;
  width: ${(p) => (p.$compact ? 'auto' : '100%')};
  max-width: ${(p) => (p.$compact ? '240px' : 'none')};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: ${(p) => (p.$compact ? '6px 12px' : '14px 16px')};
  border-radius: ${(p) => (p.$compact ? '999px' : '12px')};
  border: 1px solid ${(p) => (p.$open ? COLORS.ctaBorder : COLORS.panelBorder)};
  background: ${COLORS.surfaceInset};
  color: ${COLORS.text};
  font-size: ${(p) => (p.$compact ? '14px' : '16px')};
  font-family: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  /* A phone shows the glyph alone, in a round button the size of the header's other icon buttons. */
  ${(p) =>
    p.$mobileIcon &&
    css`
      ${MOBILE} {
        justify-content: center;
        width: ${SPACE[40]};
        height: ${SPACE[40]};
        max-width: none;
        padding: ${SPACE[0]};

        & > :not(:first-child) {
          display: none;
        }
      }
    `}
`;

/** The glyph an icon trigger shows on a phone; hidden from a desktop's labelled trigger. */
export const MobileIcon = styled.span`
  display: none;
  color: ${COLORS.textMuted};

  ${MOBILE} {
    display: inline-flex;
  }
`;

export const Value = styled(Text).attrs({ tag: 'span' as const })`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const Chevron = styled.svg<{ $open: boolean }>`
  flex: 0 0 auto;
  width: 12px;
  height: 12px;
  fill: none;
  stroke: ${COLORS.textMuted};
  stroke-width: 2;
  transition: transform ${ANIM.INTERACT_MS}ms ${ANIM.EASE};
  transform: rotate(${(p) => (p.$open ? '180deg' : '0deg')});
`;

/** The options flyout. Portaled to <body> and positioned as `position: fixed` off the trigger's screen rect
 *  (the trigger can live inside React Flow's transformed canvas, so an in-flow absolute menu would be scaled +
 *  clipped). Rounded + soft-elevated to match the studio's other popovers; the coords + max-height are supplied
 *  inline by the component. */
export const Menu = styled.div`
  position: fixed;
  z-index: 2147483001;
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 6px;
  border-radius: 14px;
  background: ${COLORS.surfaceCard};
  border: 1px solid ${COLORS.panelBorder};
  box-shadow: ${COLORS.menuShadow};
  overflow-y: auto;
  overscroll-behavior: contain;
`;

export const Option = styled.button<{ $selected: boolean; $active: boolean; $rich?: boolean }>`
  display: flex;
  ${(p) => (p.$rich ? 'flex-direction: column; align-items: flex-start; gap: 1px;' : 'align-items: center;')}
  width: 100%;
  text-align: left;
  padding: 10px 12px;
  border: 0;
  border-radius: 10px;
  cursor: pointer;
  background: ${(p) => (p.$selected ? COLORS.ctaTint : p.$active ? COLORS.hoverWash : 'transparent')};
  color: ${(p) => (p.$selected ? COLORS.ctaSolid : COLORS.text)};
  font-size: 14px;
  font-weight: ${(p) => (p.$selected ? 600 : 400)};
  font-family: inherit;
  transition: background ${ANIM.INTERACT_MS}ms ${ANIM.EASE};

  &:hover {
    background: ${(p) => (p.$selected ? COLORS.ctaTint : COLORS.hoverWash)};
  }
`;

export const OptionText = styled(Text).attrs({ tag: 'span' as const })`
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
